// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import { MediaPreviewComponent } from '@epicuro-next/ataraxia/media-preview';
import {
  recommendationsPanelsLocaleKeys,
  spotlightBasketMarketingPanelHeading,
} from '@epicuro-next/constants/features/localization';
import {
  IDynamicBindableValue,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import {
  openConfigStripEventHandler,
  recommendationItemClickedEventHandler,
  recommendationItemShownEventHandler,
} from '../../events';
import {
  errorMessage,
  hasError,
  isLoading,
} from '../../loading-state-wrapper-helpers';
import { conditionalMessage } from '../../misc';
import { getPanelTrackingIdProperty } from '../../panel-tracking-id';
import { getServingConfigProperty } from '../../serving-config';

import {
  hasData,
  hasNoItems,
  httpBodyToProductData,
  productData,
} from './../../product-data';

const panelNameProvider: IDynamicBindableValue = {
  provider: 'id:search-product-root',
  property: 'panelName',
};
const panelStyleProvider: IDynamicBindableValue = {
  provider: 'id:search-product-container',
  property: 'panelTrackingId',
};

const header: IDynamicSimpleText = {
  id: 'header',
  contentTypeId: 'dynamic-elements-simple-text',
  styles: {
    classes: ['font-weight-bold'],
    style: {
      'font-size': '18px',
    },
    variables: {
      '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
    },
  },
  inputs: {
    text: spotlightBasketMarketingPanelHeading,
  },
};

const loadingSkeletonItem: IDynamicItem = {
  id: 'loading-skeleton-item',
  contentTypeId: 'dynamic-collections-renderer',
  styles: {
    style: {
      height: '180px',
      width: '180px',
      'min-width': '180px',
      'border-radius': 'var(--border-radius-lg, .25rem)',
      'background-color': '#f5f5f5',
    },
    classes: ['m-2', 'cursor-pointer', 'd-block'],
  },
};

const loadingSkeletons: IDynamicItem = {
  id: 'loading-skeletons-array-renderer',
  contentTypeId: 'dynamic-elements-array-renderer',
  if: [
    'EQ',
    [
      true,
      {
        provider: 'parent',
        property: 'isLoading',
      },
    ],
  ],
  styles: {
    classes: ['d-flex', 'flex-row', 'flex-shrink-0'],
  },
  data: {
    item: [{}],
  },
  inputs: {
    propertyToUse: 'productData',
    contentType: loadingSkeletonItem,
    items$: {
      provider: 'self',
      property: 'item',
      transform: function (item: {}) {
        const nrOfSkeletons = 20;
        return Array(nrOfSkeletons).fill(item);
      }.toString(),
    },
  },
};

const itemMediaPreview: IDynamicItem = {
  id: 'item-media-preview',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    style: {
      width: '180px',
      'border-top-left-radius': 'var(--border-radius-lg, .25rem)',
      'border-top-right-radius': 'var(--border-radius-lg, .25rem)',
    },
    classes: ['m-2', 'cursor-pointer', 'd-block'],
  },
  inputs: {
    media: {
      provider: 'self',
      property: 'productData',
      transform: function (productDataArg: {
        media: MediaPreviewComponent['media'];
      }) {
        return productDataArg.media;
      }.toString(),
    },
  },
  eventHandlers: {
    onClick: [
      recommendationItemClickedEventHandler(
        panelNameProvider,
        panelStyleProvider,
        {
          provider: 'self',
          property: 'productData',
        },
      ),
      openConfigStripEventHandler('self'),
    ],
    onVisibleOnce: [
      recommendationItemShownEventHandler(
        panelNameProvider,
        panelStyleProvider,
        {
          provider: 'self',
          property: 'productData',
        },
      ),
    ],
  },
};

const itemsArrayRenderer: IDynamicItem = {
  id: 'items-array-renderer',
  contentTypeId: 'dynamic-elements-array-renderer',
  if: [
    'EQ',
    [
      false,
      {
        provider: 'parent',
        property: 'isLoading',
      },
    ],
  ],
  styles: {
    classes: ['d-flex', 'flex-row', 'flex-shrink-0'],
  },
  data: {
    productData: {
      provider: 'parent',
      property: 'productData',
    },
  },
  inputs: {
    items$: {
      provider: 'self',
      property: 'productData',
    },
    propertyToUse: 'productData',
    contentType: itemMediaPreview,
  },
};

const getRecommendedItemsData = {
  loadingStateWrapper: {
    provider: 'HttpBodyProvider',
    property: {
      values: [
        panelNameProvider,
        {
          provider: 'GlobalStoreProviderService',
          property: {
            once: true,
            paths: [
              'accounts.activeAccount.domainId',
              'accounts.activeAccount.brandOrg',
              'productBasket.currentPresetValue',
              'basket.tracking-payloads.payloads.search',
            ],
          },
        },
      ],
      transform: function ([panelName, data]: [
        string,
        // tslint:disable-next-line:no-any
        [string, string, string, any],
      ]): string {
        const domainId = data[0];
        const brandOrg = data[1];
        const orderType = data[2] === 'now' ? 'reorder' : 'preorder';
        const brandOrgOrDomainId = brandOrg ?? domainId;

        const userEvent = data[3];

        userEvent.eventType = 'shopping-cart-page-view';

        delete userEvent.searchQuery;

        const body = {
          panelType: panelName,
          properties: {
            brandOrg: brandOrgOrDomainId,
            orderType,
            userEvent,
          },
        };

        function createFetchString(bodyArg: object): string {
          const headersJson = JSON.stringify({
            accept: 'application/json, text/plain, */*',
            'accept-language': 'en-US',
            'content-type': 'application/json',
          });

          const bodyJson = JSON.stringify(JSON.stringify(bodyArg));

          const url = '/api/v2/recommendation/suggest';
          const method = 'POST';
          return `fetch("${url}", { "headers": ${headersJson}, "body": ${bodyJson}, "method": "${method}" });`;
        }

        return createFetchString(body);
      }.toString(),
    },
    transform: httpBodyToProductData.toString(),
    wrapWithLoadingState: true,
  },
  isLoading: isLoading(),
  ...getServingConfigProperty(),
  ...getPanelTrackingIdProperty({
    page: 'pb',
    location: 'sp',
    type: 'hr',
    provider: 'gcrv',
    cssVariant: '001',
  }),
  productData: productData(),
  hasError: hasError(),
  hasData: hasData('self'),
  hasNoItems: hasNoItems(),
};

const scrollableContainer: IDynamicItem = {
  id: 'scrollable-container',
  contentTypeId: 'dynamic-elements-scrollable-container',
  if: [
    'EQ',
    [
      false,
      {
        provider: 'parent',
        property: 'hasError',
      },
    ],
  ],
  data: {
    productData: {
      provider: 'parent',
      property: 'productData',
    },
    isLoading: {
      provider: 'parent',
      property: 'isLoading',
    },
  },
  inputs: {
    showScrollButtons: true,
  },
  children: [loadingSkeletons, itemsArrayRenderer],
};

const searchProductContainer: IDynamicItem = {
  id: 'search-product-container',
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    ...getRecommendedItemsData,
  },
  children: [
    scrollableContainer,
    errorMessage(),
    conditionalMessage(
      'no-items-message',
      'parent',
      'hasNoItems',
      recommendationsPanelsLocaleKeys.noRecommendedItemsReturned,
    ),
  ],
};

const payloadIsReadyWrapper: IDynamicItem = {
  id: 'payload-is-ready-wrapper',
  contentTypeId: 'dynamic-collections-renderer',
  if: [
    'EQ',
    [
      true,
      {
        provider: 'parent',
        property: 'isPayloadReady',
      },
    ],
  ],
  styles: {
    classes: ['d-block', 'p-3'],
  },
  children: [
    {
      contentTypeId: 'dynamic-collections-renderer',
      children: [header],
    },
    searchProductContainer,
  ],
};

export const searchProducts: IDynamicItem = {
  id: 'search-product-root',
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    panelName: 'EmptySearchResult',
    isPayloadReady: {
      provider: 'GlobalStoreProviderService',
      property: {
        paths: ['basket.tracking-payloads.payloads.search'],
      },
      transform: function (data: unknown): boolean {
        return Boolean(data);
      }.toString(),
    },
  },
  children: [payloadIsReadyWrapper],
};
